import React, { Fragment, type ReactElement, type ReactNode } from "react";
import { cn } from "@ndd/ui";
import type { RdSurfacePath, RdTab, RdTabId, RdTabLink } from "../../lib/rd-page-tabs";
import { THEMED_RULE } from "../../lib/always-look";

// The RD page's tab row and Profile's sub-link row in the single-rail frame
// (NO-754). A leaf module (`import React`, relative imports, no router, no
// queries) so `tsx --test` renders it; the composer (rd-page-frame.tsx)
// supplies real links through `renderLink`.

export interface RdTabLinkProps {
  to: RdSurfacePath;
  current: boolean;
  className: string;
  children: ReactNode;
}

export type RenderRdTabLink = (props: RdTabLinkProps) => ReactElement;

function tabClass(current: boolean): string {
  return cn(
    "-mb-px inline-flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 font-sans text-body-lg transition-colors",
    current
      ? "border-foreground font-semibold text-foreground brand:font-bold"
      : "border-transparent font-medium text-muted-foreground hover:text-foreground"
  );
}

function subLinkClass(current: boolean): string {
  return cn(
    "inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 font-sans text-body transition-colors",
    current
      ? "bg-accent font-semibold text-foreground"
      : "font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
  );
}

function LinkLabel({ link }: { link: RdTabLink }): ReactElement {
  return (
    <>
      <span>{link.label}</span>
      {link.count === undefined ? null : (
        <span className="text-xs font-medium text-subtle">{link.count}</span>
      )}
    </>
  );
}

/** The tabs, with the current one marked for assistive tech and by its underline. */
export function RdPageTabsNav({
  tabs,
  active,
  renderLink,
}: {
  tabs: RdTab[];
  active: RdTabId | null;
  renderLink: RenderRdTabLink;
}): ReactElement {
  return (
    <nav
      aria-label="RD sections"
      className={cn("flex overflow-x-auto border-b border-border", THEMED_RULE)}
    >
      {tabs.map((tab) => (
        <Fragment key={tab.id}>
          {renderLink({
            to: tab.to,
            current: tab.id === active,
            className: tabClass(tab.id === active),
            children: <LinkLabel link={tab} />,
          })}
        </Fragment>
      ))}
    </nav>
  );
}

/** Profile's second row: Functional forms and Observations, shown on Profile only. */
export function RdProfileSubnav({
  links,
  active,
  renderLink,
}: {
  links: RdTabLink[];
  active: RdSurfacePath | null;
  renderLink: RenderRdTabLink;
}): ReactElement {
  return (
    <nav aria-label="Profile sections" className="flex gap-1 pt-3">
      {links.map((link) => (
        <Fragment key={link.to}>
          {renderLink({
            to: link.to,
            current: link.to === active,
            className: subLinkClass(link.to === active),
            children: <LinkLabel link={link} />,
          })}
        </Fragment>
      ))}
    </nav>
  );
}
